import { useCallback, useMemo } from "react";

import { useDataContext } from "./DataProvider";
import type { iCmsDynamicVariable } from "./types";
import {
  CME_OPTIONSET_BODY,
  CME_OPTIONSET_COLOUR,
  CME_OPTIONSET_DRIVETRAIN,
  CME_OPTIONSET_ENGINE,
  CME_OPTIONSET_GRADE,
  CME_OPTIONSET_MVOB,
  CME_OPTIONSET_PACKS,
  CME_OPTIONSET_TONE,
  CME_OPTIONSET_TRANSMISSION,
  CME_OPTIONSET_TRIM,
  FSL,
} from "./static";
import { useCmeContext } from "../fe-cme/CMEProvider";
import useUrlParamLinks from "./useUrlParamLinks";

type iGlobalParams = {
  dealershipPhone: string;
  vehicleName: string;
  year: number;
  vehicleStockAvailable: boolean;
  userName: string;
  configLink: any;
  hybrid: string;
  month: string;
  dealerLastName: string;
  grade: string;
  body: string;
  drivetrain: string;
  transmission: string;
  colour: string;
  engine: string;
  interiorTrim: string;
  optionPack: string;
  mvob: string;
  dealerFirstName: string;
  dealershipName: string;
  dealershipAddress: string;
  noStock: boolean;
  unavailable: boolean;
};

const useGlobalParams = () => {
  const {
    dealershipData,
    dealerData,
    userData,
    cmeSiloUiData,
    isHybridEngine,
    dynamicVariables,
    vehicleStockData,
    currentPricingType,
  } = useDataContext();
  const { cmeOptions } = useCmeContext();
  const { createTFYLink } = useUrlParamLinks();

  const globalParams: iGlobalParams = useMemo(
    () => ({
      dealershipName: dealershipData?.name || "",
      dealershipAddress: dealershipData?.address || "",
      dealershipPhone: dealershipData?.phone || "",
      dealerFirstName: dealerData?.firstName || "",
      dealerLastName: dealerData?.lastName || "",
      userName: userData?.name || "",
      vehicleName: cmeSiloUiData?.model?.title || "",
      grade:
        cmeSiloUiData?.features?.[CME_OPTIONSET_GRADE]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_GRADE)
        ]?.clientName || "",
      body:
        cmeSiloUiData?.features?.[CME_OPTIONSET_BODY]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_BODY)
        ]?.clientName || "",
      drivetrain:
        cmeSiloUiData?.features?.[CME_OPTIONSET_DRIVETRAIN]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_DRIVETRAIN)
        ]?.clientName || "",
      transmission:
        cmeSiloUiData?.features?.[CME_OPTIONSET_TRANSMISSION]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_TRANSMISSION)
        ]?.clientName || "",
      colour:
        cmeSiloUiData?.features?.[CME_OPTIONSET_COLOUR]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_COLOUR)
        ]?.clientName || "",
      engine:
        cmeSiloUiData?.features?.[CME_OPTIONSET_ENGINE]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_ENGINE)
        ]?.clientName || "",
      interiorTrim:
        cmeSiloUiData?.features?.[CME_OPTIONSET_TRIM]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_TRIM)
        ]?.clientName || "",
      optionPack:
        cmeSiloUiData?.features?.[CME_OPTIONSET_PACKS]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_PACKS)
        ]?.clientName || "",
      tone:
        cmeSiloUiData?.features?.[CME_OPTIONSET_TONE]?.options?.[
          cmeOptions?.selectedOptions?.get(CME_OPTIONSET_TONE)
        ]?.clientName || "",
      mvob: cmeOptions?.selectedOptions?.get(CME_OPTIONSET_MVOB),
      month: new Date().toLocaleString([], {
        month: "long",
      }),
      year: new Date().getFullYear(),
      hybrid: isHybridEngine ? `<span class="Hybrid">Hybrid</span>` : "",
      vehicleStockAvailable: vehicleStockData?.available || false,
      isFsl: currentPricingType === FSL,
      configLink: createTFYLink(),
      noStock: vehicleStockData?.numberInStock === 0,
      unavailable: vehicleStockData && !vehicleStockData.canOrder,
    }),
    [
      vehicleStockData,
      currentPricingType,
      dealershipData?.name,
      dealershipData?.address,
      dealershipData?.phone,
      dealerData?.firstName,
      dealerData?.lastName,
      userData?.name,
      isHybridEngine,
      cmeSiloUiData?.model.title,
      cmeSiloUiData?.features,
      cmeOptions?.selectedOptions,
      createTFYLink,
    ],
  );

  const getDynamicVariable = useCallback(
    (labelCode: string) => dynamicVariables?.get(labelCode) ?? `{${labelCode}}`,
    [dynamicVariables],
  );

  // @ts-ignore
  const getGlobalParam = useCallback((key: string) => globalParams[key], [globalParams]);

  const getCustomValue = useCallback(
    (dynamicVariable: iCmsDynamicVariable) => {
      let customValue = "";

      for (const override of dynamicVariable.custom || []) {
        for (const condition of override.criteria) {
          const fieldName = condition.fieldName;
          let fieldValue = getGlobalParam(fieldName);

          const isBooleanValue = typeof fieldValue === "boolean";
          fieldValue = isBooleanValue ? fieldValue.toString() : fieldValue;

          const isEmptyCondition = !fieldValue && !condition.equalsTo;

          if (
            isEmptyCondition ||
            condition.equalsTo?.toLowerCase() === fieldValue?.toLowerCase()
          ) {
            customValue = override.value;
          } else {
            customValue = "";
            break;
          }
        }

        if (customValue) {
          return customValue;
        }
      }
      return customValue;
    },
    [getGlobalParam],
  );

  const replaceCmsDynamicVariables = useCallback(
    (text: string) => {
      // Regex: matches {placeholder} with 1–100 non-brace {} and non-newline \n characters inside
      const DYNAMIC_VARIABLE_REGEX = /\{([^{}\n]{1,100})\}/g;
      const matches = text.match(DYNAMIC_VARIABLE_REGEX);

      let result = text;

      if (matches) {
        const regExp = /\w+/;
        const dynamicVariables: string[] = matches.map((match) => regExp.exec(match)![0]);

        for (const key of dynamicVariables) {
          const dynamicVariable: iCmsDynamicVariable | string = getDynamicVariable(key);

          if (typeof dynamicVariable !== "string") {
            const defaultValue = dynamicVariable.value;
            const customValue = getCustomValue(dynamicVariable);
            result = result.replace(`{${key}}`, customValue || defaultValue);
          }
        }
      }
      return result;
    },
    [getDynamicVariable, getCustomValue],
  );

  const replaceGlobalParams = useCallback(
    (text: string) => {
      let result = text;

      for (const key of Object.keys(globalParams)) {
        result = result.replace(`{${key}}`, getGlobalParam(key));
      }

      result = replaceCmsDynamicVariables(result);
      return result;
    },
    [globalParams, replaceCmsDynamicVariables, getGlobalParam],
  );

  const injectGlobalParams = useCallback(
    (props: any) => {
      for (const key of Object.keys(props)) {
        if (typeof props[key] === "string" && props[key].includes("{")) {
          props[key] = replaceGlobalParams(props[key]);
        }
        // object
        if (typeof props[key] === "object" && props[key]) {
          props[key] = injectGlobalParams(props[key]);
        }
        // Verifying again to see if there is still variables to be replaced as it's supports nested variables
        if (typeof props[key] === "string" && props[key].includes("{")) {
          props[key] = replaceGlobalParams(props[key]);
        }
      }
      return props;
    },
    [replaceGlobalParams],
  );

  return { injectGlobalParams, replaceGlobalParams };
};
export default useGlobalParams;
